import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  keywords: string[];
  createdAt: string;
  updatedAt: string;
}

interface FAQManagementProps {
  tenantId: string | null;
}

const FAQManagement: React.FC<FAQManagementProps> = ({ tenantId }) => {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [showFAQModal, setShowFAQModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<FAQ | null>(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    keywords: '',
  });
  const [faqLoading, setFaqLoading] = useState(false);
  const [faqError, setFaqError] = useState('');
  const [faqSuccess, setFaqSuccess] = useState('');

  // Fetch FAQs
  const fetchFAQs = async () => {
    if (!tenantId) return;

    setFaqsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/faq`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFaqs(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setFaqsLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchFAQs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // FAQ handlers
  const handleFAQCreateClick = () => {
    setEditingFAQ(null);
    setFaqForm({ question: '', answer: '', keywords: '' });
    setFaqError('');
    setFaqSuccess('');
    setShowFAQModal(true);
  };

  const handleFAQEditClick = (faq: FAQ) => {
    setEditingFAQ(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      keywords: faq.keywords.join(', '),
    });
    setFaqError('');
    setFaqSuccess('');
    setShowFAQModal(true);
  };

  const handleFAQSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFaqLoading(true);
    setFaqError('');
    setFaqSuccess('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setFaqError('Authentication required. Please log in again.');
        return;
      }

      const keywords = faqForm.keywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k.length > 0);

      const url = editingFAQ
        ? `${API_URL}/faq/${editingFAQ.id}`
        : `${API_URL}/faq`;
      const method = editingFAQ ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: faqForm.question,
          answer: faqForm.answer,
          keywords: keywords.length > 0 ? keywords : undefined,
        }),
      });

      if (response.ok) {
        setFaqSuccess(editingFAQ ? 'FAQ updated successfully!' : 'FAQ created successfully!');
        fetchFAQs();
        setTimeout(() => {
          setShowFAQModal(false);
          setFaqSuccess('');
          setEditingFAQ(null);
        }, 1500);
      } else {
        const errorData = await response.json();
        setFaqError(errorData.error?.message || `Failed to ${editingFAQ ? 'update' : 'create'} FAQ`);
      }
    } catch (error) {
      console.error(`Failed to ${editingFAQ ? 'update' : 'create'} FAQ:`, error);
      setFaqError(`Failed to ${editingFAQ ? 'update' : 'create'} FAQ`);
    } finally {
      setFaqLoading(false);
    }
  };

  const handleFAQDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.alert('Authentication required. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/faq/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok || response.status === 204) {
        fetchFAQs();
      } else {
        const errorData = await response.json();
        window.alert(errorData.error?.message || 'Failed to delete FAQ');
      }
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      window.alert('Failed to delete FAQ');
    }
  };

  return (
    <>
      {/* FAQ Management Section */}
      <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-8 shadow-xl backdrop-blur-sm">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white tracking-tight">
              FAQ Management
            </h2>
            {faqs.length > 0 && (
              <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                {faqs.length} {faqs.length === 1 ? 'item' : 'items'}
              </span>
            )}
          </div>
          <button
            onClick={handleFAQCreateClick}
            className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-semibold py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add FAQ
          </button>
        </div>

        {faqsLoading ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-main-light/30 rounded-full mb-4 animate-pulse">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg font-medium">Loading FAQs...</p>
          </div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-main-light/30 rounded-full mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-400 text-lg font-medium">No FAQs yet</p>
            <p className="text-gray-500 text-sm mt-2">Click "Add FAQ" to create your first FAQ entry</p>
          </div>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-main-dark/50 backdrop-blur-sm rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.01] border border-white/5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-green-600 to-green-700 rounded-lg shadow-md flex-shrink-0">
                        <span className="text-white font-bold text-xs">Q</span>
                      </div>
                      <h3 className="text-lg font-bold text-white">{faq.question}</h3>
                    </div>
                    <p className="text-gray-400 text-sm mb-3 whitespace-pre-wrap ml-11">{faq.answer}</p>
                    {faq.keywords && faq.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3 ml-11">
                        {faq.keywords.map((keyword, idx) => (
                          <span
                            key={idx}
                            className="bg-purple-500/20 text-purple-300 text-xs px-3 py-1 rounded-full font-medium border border-purple-500/30"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-gray-500 text-xs ml-11">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Created: {new Date(faq.createdAt).toLocaleDateString()}
                      {faq.updatedAt !== faq.createdAt && (
                        <span className="flex items-center gap-1">
                          <span>•</span>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Updated: {new Date(faq.updatedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleFAQEditClick(faq)}
                      className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-1.5 px-4 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-1.5 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => handleFAQDelete(faq.id)}
                      className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-semibold py-1.5 px-4 rounded-lg text-sm shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-1.5 hover:scale-105 active:scale-95"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ Modal */}
      {showFAQModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-main-medium via-main-medium to-main-dark rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">
                {editingFAQ ? 'Edit FAQ' : 'Create New FAQ'}
              </h3>
              <button
                onClick={() => {
                  setShowFAQModal(false);
                  setFaqError('');
                  setFaqSuccess('');
                  setEditingFAQ(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleFAQSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Question *
                  </label>
                  <input
                    type="text"
                    value={faqForm.question}
                    onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    placeholder="Enter the question..."
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Answer *
                  </label>
                  <textarea
                    value={faqForm.answer}
                    onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    placeholder="Enter the answer..."
                    rows={6}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={faqForm.keywords}
                    onChange={(e) => setFaqForm({ ...faqForm, keywords: e.target.value })}
                    className="w-full bg-main-dark/50 backdrop-blur-sm rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-main-dark/70 transition-all duration-200 shadow-md"
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Separate keywords with commas. Keywords help improve FAQ matching.
                  </p>
                </div>
                {faqError && (
                  <div className="p-4 bg-red-500/10 backdrop-blur-sm rounded-xl text-red-200 text-sm shadow-lg border-l-4 border-red-500">
                    {faqError}
                  </div>
                )}
                {faqSuccess && (
                  <div className="p-4 bg-green-500/10 backdrop-blur-sm rounded-xl text-green-200 text-sm shadow-lg border-l-4 border-green-500">
                    {faqSuccess}
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowFAQModal(false);
                      setFaqError('');
                      setFaqSuccess('');
                      setEditingFAQ(null);
                    }}
                    className="flex-1 bg-main-light/50 hover:bg-main-light/70 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={faqLoading}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-semibold py-2.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                  >
                    {faqLoading ? 'Saving...' : editingFAQ ? 'Update FAQ' : 'Create FAQ'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FAQManagement;
